/**
 * Admin Portal App
 *
 * Authenticates against USERS Cognito pool.
 * Routes at root level (no /admin prefix) for admin.geauxfile.com deployment.
 */

import { BrandFooter } from '@portal/components/shared/BrandFooter';
import { AdminGuard } from '@portal/guards/AdminGuard';
import { AdminDashboard } from '@portal/pages/admin/Dashboard';
import { ForgotPassword } from '@portal/pages/ForgotPassword';
import { Login } from '@portal/pages/Login';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminLayout } from './layouts/AdminLayout';

// Lazy-loaded admin pages
const AdminAutoqueries = lazy(() => import('@portal/pages/admin/Autoqueries').then(m => ({ default: m.AdminAutoqueries })));
const BulkUpload = lazy(() => import('@portal/pages/admin/BulkUpload').then(m => ({ default: m.BulkUpload })));
const AdminControlRoom = lazy(() => import('@portal/pages/admin/ControlRoom').then(m => ({ default: m.AdminControlRoom })));
const AdminCustomerDetail = lazy(() => import('@portal/pages/admin/CustomerDetail').then(m => ({ default: m.AdminCustomerDetail })));
const AdminCustomers = lazy(() => import('@portal/pages/admin/Customers').then(m => ({ default: m.AdminCustomers })));
const AdminEINWizard = lazy(() => import('@portal/pages/admin/EINWizard').then(m => ({ default: m.AdminEINWizard })));
const AdminFulfillment = lazy(() => import('@portal/pages/admin/Fulfillment').then(m => ({ default: m.AdminFulfillment })));
const AdminFulfillmentDetail = lazy(() => import('@portal/pages/admin/FulfillmentDetail').then(m => ({ default: m.AdminFulfillmentDetail })));
const AdminNotifications = lazy(() => import('@portal/pages/admin/Notifications').then(m => ({ default: m.AdminNotifications })));
const AdminRAAddresses = lazy(() => import('@portal/pages/admin/RAAddresses').then(m => ({ default: m.AdminRAAddresses })));
const AdminRACRequests = lazy(() => import('@portal/pages/admin/RACRequests').then(m => ({ default: m.AdminRACRequests })));
const AdminServiceOfferings = lazy(() => import('@portal/pages/admin/ServiceOfferings').then(m => ({ default: m.AdminServiceOfferings })));
const AdminServices = lazy(() => import('@portal/pages/admin/Services').then(m => ({ default: m.AdminServices })));
const AdminSOSRequests = lazy(() => import('@portal/pages/admin/SOSRequests').then(m => ({ default: m.AdminSOSRequests })));
const AdminSupport = lazy(() => import('@portal/pages/admin/Support').then(m => ({ default: m.AdminSupport })));
const AdminSupportTicketDetail = lazy(() => import('@portal/pages/admin/SupportTicketDetail').then(m => ({ default: m.AdminSupportTicketDetail })));
const AdminTemplates = lazy(() => import('@portal/pages/admin/Templates').then(m => ({ default: m.AdminTemplates })));
const AdminUsers = lazy(() => import('@portal/pages/admin/Users').then(m => ({ default: m.AdminUsers })));

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 *
 */
export default function App() {
  return (
    <><Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login skipGuestLink isAdmin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* EIN Wizard — unguarded operator tool */}
        <Route path="/ein-wizard" element={<AdminEINWizard />} />

        {/* Admin routes - protected by AdminGuard, ROOT level paths */}
        <Route element={<AdminGuard><AdminLayout /></AdminGuard>}>
          <Route index element={<AdminDashboard />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="offerings" element={<AdminServiceOfferings />} />
          <Route path="templates" element={<AdminTemplates />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="customers/:customerId" element={<AdminCustomerDetail />} />
          <Route path="ra-addresses" element={<AdminRAAddresses />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="fulfillment" element={<AdminFulfillment />} />
          <Route path="fulfillment/:id" element={<AdminFulfillmentDetail />} />
          <Route path="sos" element={<AdminSOSRequests />} />
          <Route path="rac" element={<AdminRACRequests />} />
          <Route path="autoqueries" element={<AdminAutoqueries />} />
          <Route path="ein-wizard" element={<AdminEINWizard />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="support/:ticketId" element={<AdminSupportTicketDetail />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="control-room" element={<AdminControlRoom />} />
          <Route path="bulk-upload" element={<BulkUpload />} />
        </Route>

        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense><BrandFooter /></>
  );
}
